// Adyvance Wallet → Refer a shop (PARTNER-001, owner 2026-10-25; D-067): the shop's own referral code
// and sign-up link, what it earns (the programme's percent of each plan payment of a shop it brought,
// for the programme's months, as wallet credit) and the shops it brought. Hidden while Adyvance has
// the programme off; the invoice popup for Settings → Billing is here too.
import { useQuery } from "@tanstack/react-query";
import { formatDate, formatMoney } from "@ady/format";
import { t } from "@ady/i18n";
import { cx, Icon, InvoiceDocument, Popup, useToast } from "@ady/ui";
import { adyWalletApi } from "./walletApi";

export function ReferShopCard({ orgId }: { orgId: string }) {
  const toast = useToast();
  const q = useQuery({
    queryKey: ["wallet", orgId, "referral"],
    queryFn: () => adyWalletApi.referral(orgId),
    retry: false,
  });
  const d = q.data;
  if (!d || !d.settings.enabled) return null;
  const link = `${location.origin}/sign-in?ref=${encodeURIComponent(d.code)}`;
  const copy = (text: string) =>
    void navigator.clipboard
      ?.writeText(text)
      .then(() => toast.show({ tone: "success", title: t("billingOps.shop.referral.copied") }));
  return (
    <section
      className="ady-bg-brand-tint flex flex-col gap-2 rounded-sm border border-card p-3"
      data-testid="refer-shop"
    >
      <header className="flex items-center gap-2">
        <span className="flex size-8 items-center justify-center rounded-full bg-surface">
          <Icon name="gift" gradient />
        </span>
        <span className="flex flex-col">
          <h2 className="text-sm font-semibold">{t("billingOps.shop.referral.title")}</h2>
          <span className="text-xs text-fg-secondary">
            {t("billingOps.shop.referral.lead", {
              percent: d.settings.percent,
              months: d.settings.months,
            })}
          </span>
        </span>
      </header>
      <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_12rem]">
        <span className="flex items-center gap-2 rounded-xs border border-card bg-surface px-2.5 py-1.5">
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="text-2xs text-fg-tertiary">{t("billingOps.shop.referral.code")}</span>
            <span className="truncate font-mono text-sm font-bold text-link">{d.code}</span>
          </span>
          <button
            type="button"
            className="text-xs font-semibold text-link"
            onClick={() => copy(d.code)}
          >
            {t("billingOps.shop.referral.copy")}
          </button>
        </span>
        <span className="flex items-center gap-2 rounded-xs border border-card bg-surface px-2.5 py-1.5">
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="text-2xs text-fg-tertiary">{t("billingOps.shop.referral.link")}</span>
            <span className="truncate text-xs">{link}</span>
          </span>
          <button
            type="button"
            className="text-xs font-semibold text-link"
            onClick={() => copy(link)}
          >
            {t("billingOps.shop.referral.copy")}
          </button>
        </span>
        <span className="flex flex-col rounded-xs border border-card bg-surface px-2.5 py-1.5">
          <span className="text-2xs text-fg-tertiary">{t("billingOps.shop.referral.earned")}</span>
          <span className="text-sm font-bold tabular-nums">{formatMoney(d.earned_paisa)}</span>
        </span>
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-2xs font-bold tracking-wide text-fg-tertiary uppercase">
          {t("billingOps.shop.referral.shops")}
        </span>
        {d.shops.length === 0 ? (
          <p className="text-xs text-fg-secondary">{t("billingOps.shop.referral.none")}</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {d.shops.map((s, i) => (
              <li
                key={i}
                className="flex items-center gap-2 rounded-xs bg-surface px-2.5 py-1 text-xs"
              >
                <span className="flex-1 font-medium">{s.name}</span>
                <span className={cx("text-2xs", s.paying ? "text-success-fg" : "text-fg-tertiary")}>
                  {s.paying && s.until
                    ? t("billingOps.shop.referral.until", { date: formatDate(s.until) })
                    : t("billingOps.shop.referral.ended")}
                </span>
                <span className="w-20 text-end font-semibold tabular-nums">
                  {formatMoney(s.earned_paisa)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

/** One Adyvance invoice (Settings → Billing → Invoices → View). */
export function InvoicePopup({
  orgId,
  invoiceId,
  onClose,
}: {
  orgId: string;
  invoiceId: string | null;
  onClose: () => void;
}) {
  const q = useQuery({
    queryKey: ["wallet", orgId, "invoice", invoiceId],
    queryFn: () => adyWalletApi.invoice(orgId, invoiceId!),
    enabled: Boolean(invoiceId),
  });
  return (
    <Popup open={invoiceId !== null} onClose={onClose} label={t("billingOps.doc.title")} size="lg">
      {q.data ? (
        <InvoiceDocument data={q.data} onClose={onClose} />
      ) : (
        <div className="h-48 animate-pulse" />
      )}
    </Popup>
  );
}
